import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, booleanAttribute, computed, inject, input, output, signal } from '@angular/core';

import { EventMediaHelperService, EventMediaTracking } from '@frontend/sports/betting-offer/feature/media';
import { EventDetailsColumnType } from '@frontend/sports/betting-offer/feature/model';
import { MediaConfig } from '@frontend/sports/common/client-config-data-access';
import { getAnimationIconForSport, getStatsIcon, getVideoIcon } from '@frontend/sports/common/core/feature/icon';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { EventMediaService } from '@frontend/sports/grid/core/feature/event-media';
import { GridEvent, GridMedia } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { EventMediaTrackingService } from './event-media-tracking.service';

@Component({
    selector: 'ms-event-media',
    templateUrl: './event-media.component.html',
    styleUrls: ['./event-media.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [EventMediaTrackingService],
    imports: [NgClass, IconCustomComponent, DsButtonIcon],
})
export class EventMediaComponent {
    private readonly eventMediaService = inject(EventMediaService);
    private readonly gridConfig = inject<GridConfig | null>(GRID_CONFIG, { optional: true });

    event = input.required<GridEvent>();
    media = input.required<GridMedia>();
    tracking = input.required<TrackingSource>();
    betBuilderEpcotEnabled = input(false, { transform: booleanAttribute });
    isEventHovered = input(false, { transform: booleanAttribute });
    compactScrobeoard = input<boolean>(false);
    statsClicked = output<void>();
    @HostBinding('class') className = 'grid-media';
    mediaType = EventDetailsColumnType;
    hoveredType = signal<EventDetailsColumnType | undefined>(undefined);
    private readonly mediaConfig = inject(MediaConfig);
    private readonly eventMediaHelperService = inject(EventMediaHelperService);

    @HostBinding('class.hidden') get isHidden(): boolean {
        return !!this.gridConfig && !this.gridConfig.areEmptyEventStatisticsVisible && !(this.hasAnimation() || this.showStats() || this.hasVideo());
    }

    hasVideo = computed(() => {
        return this.eventMediaService.hasVideo(this.event());
    });

    hasAnimation = computed(() => {
        return this.eventMediaService.hasAnimation(this.event());
    });

    showStats = computed(() => {
        return (
            this.mediaConfig.showStatsCenterButton &&
            !this.eventMediaHelperService.hasPerformPlayerEnabled(this.event()) &&
            this.eventMediaService.hasStats(this.event())
        );
    });

    statsActive = computed(() => {
        return this.eventMediaService.statsActive(this.event(), this.media());
    });

    videoActive = computed(() => {
        return this.eventMediaService.videoActive(this.event(), this.media());
    });

    animationActive = computed(() => {
        return this.eventMediaService.animationActive(this.event(), this.media());
    });

    statsIconPadding = computed(() => {
        return this.hasAnimation() || this.hasVideo() ? false : true;
    });

    videoIcon = computed(() => {
        const isHovered = this.hoveredType() === this.mediaType.Video && this.isEventHovered();
        return getVideoIcon(isHovered);
    });

    statsIcon = computed(() => {
        const isHovered = this.hoveredType() === this.mediaType.Stats && this.isEventHovered();
        return getStatsIcon(isHovered);
    });

    animationIcon = computed(() => {
        const isHovered = this.hoveredType() === this.mediaType.Animation && this.isEventHovered();
        return getAnimationIconForSport(this.event().sport.id, isHovered);
    });

    toggleHover(type: EventDetailsColumnType, hovered: boolean): void {
        this.hoveredType.set(hovered ? type : undefined);
    }

    activateMedia(event: MouseEvent, activeType: EventDetailsColumnType): void {
        if (this.eventMediaService.statsActive(this.event(), this.media()) && activeType === EventDetailsColumnType.Stats) {
            this.eventMediaService.trackChanges(activeType, this.event(), this.mediaTracking(), true);
        } else {
            this.eventMediaService.activateMedia(activeType, this.event(), this.media(), false, true);
            this.eventMediaService.trackChanges(activeType, this.event(), this.mediaTracking());
        }
        this.statsClicked.emit();
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    activeVideoIcon = computed(() => {
        const isActive = this.media().videoEvent === this.event().id;
        return getVideoIcon(isActive);
    });

    activeStatsIcon = computed(() => {
        const isActive = this.media().statsEvent === this.event().id;
        return this.eventMediaService.getStatsIcon(isActive);
    });

    activeAnimationIcon = computed(() => {
        const isActive = this.media().animationEvent === this.event().id;
        return getAnimationIconForSport(this.event().sport.id, isActive);
    });

    private mediaTracking(): EventMediaTracking {
        return {
            name: this.event().name,
            additional: {
                [trackingConstants.SPORT_ID]: this.event().sport.id.toString(),
                [trackingConstants.SPORT_EVENT_ID]: this.event().id.toString(),
            },
            ...this.tracking(),
        };
    }
}
